import Cookies from "js-cookie";
import {
  CONSTANTS,
  createId,
  isDev,
} from "@theatticusapp/atticus-shared-components";
import { AUTH_ERRORS, COOKIES, SCREEN_PATHS, isLocal, isProd } from "../constants";
import { User } from "../models/User";
import { AxiosInstance } from "axios";
import { logger, callGet } from "./index";
import { Auth, Amplify } from "aws-amplify";
import { AtticusMySQL } from "./AtticusMySQL";
import { UserProfileJSON, UserProfile } from "models/UserProfile";
import { laravelApiV1 } from "./index";
import { retryOnException } from "./retries";

try {
  // We want to log in as an advisor who is impersonating a user
  if (Cookies.get("acting_as")) {
    console.log("casper mode initiated 👻  Clear your acting_as cookie and refresh to deactivate");
    Amplify.configure({
      Auth: !isProd ? {
        identityPoolId: 'us-east-2:bff57a87-6cef-4b15-889d-5565a7d0d9d7',
        region: 'us-east-2',
        userPoolId: 'us-east-2_OEtLLQNxl',
        userPoolWebClientId: '590gelbl1ad1rtc1cerlla61ks',
        signUpVerificationMethod: 'link',   // 'code' is used for Auth.confirmSignUp, 'link' is used for email link verification
      } : {
        identityPoolId: 'us-east-2:89339093-b9a2-41a2-b1d5-496be3bd693b',
        region: 'us-east-2',
        userPoolId: 'us-east-2_FK5kJVeJs',
        userPoolWebClientId: '26v6g526v394bilpecu9ircrla',
        signUpVerificationMethod: 'link',   // 'code' is used for Auth.confirmSignUp, 'link' is used for email link verification
      }
    });
  } else {
    Amplify.configure({
      Auth: {
        identityPoolId: isProd
          ? "us-east-2:89339093-b9a2-41a2-b1d5-496be3bd693b"
          : "us-east-2:bff57a87-6cef-4b15-889d-5565a7d0d9d7",
        region: "us-east-2",
        userPoolId: isProd ? "us-east-2_FK5kJVeJs" : "us-east-2_OEtLLQNxl",
        userPoolWebClientId: isProd
          ? "26v6g526v394bilpecu9ircrla"
          : "590gelbl1ad1rtc1cerlla61ks",
        signUpVerificationMethod: "link", // 'code' is used for Auth.confirmSignUp, 'link' is used for email link verification
      },
      API: {
        endpoints: [
          {
            name: "AdvisorProtectedREST",
            endpoint: "https://s088o0gyoc.execute-api.us-east-2.amazonaws.com/master",
            region: "us-east-2",
          },
          {
            name: "AdvisorPublicREST",
            endpoint: "https://odr0s6wzhh.execute-api.us-east-2.amazonaws.com/master",
            region: "us-east-2",
          }
        ]
      },
    });
  }

  Auth.configure();
  console.log("amplify/auth configured for " + (isProd ? "prod" : "non-prod"));
} catch (e) {
  console.error("unable to load cognito client, " + (e as any).message);
}

export class CognitoAuth {
  private sessionId?: string = "";
  constructor(private axios: AxiosInstance) {
    // if not in session storage will be set upon sign in
    this.setAuthToken(Cookies.get(COOKIES.authToken));
    // will be set upon app startup and set once authed
    this.setSessionId(
      window.sessionStorage.getItem(CONSTANTS.STORAGE_KEYS.LOG_SESSION_ID) ||
      createId()
    );
  }

  protected setAuthToken(authToken?: string): void {
    if (authToken) {
      Cookies.set(COOKIES.authToken, authToken, {
        domain: isDev ? window.document.location.hostname : ".weareatticus.com",
      });
      this.applySessionId();
    } else {
      Cookies.remove(COOKIES.authToken);
    }
    this.axios.defaults.headers.common["Authorization"] = authToken
      ? `Bearer ${authToken}`
      : "";
  }

  protected setSessionId(sessionId?: string): void {
    this.sessionId = sessionId;
    window.sessionStorage.setItem(
      CONSTANTS.STORAGE_KEYS.LOG_SESSION_ID,
      sessionId || ""
    );
  }

  protected applySessionId(): void {
    this.axios.defaults.headers.common["X-UI-Session-Id"] =
      this.sessionId || "";
  }

  public getSessionId(): string | undefined {
    return this.sessionId;
  }

  protected clearSession(excludeSessionStorage = false): void {
    this.setAuthToken();
    this.setSessionId();
    if (!excludeSessionStorage) {
      window.sessionStorage.clear();
    }
  }

  public isImpersonationActive(): boolean {
    return !!Cookies.get(COOKIES.impersonationCookieName)
  }

  // Delete once fully committed to hosted auth UI
  // async signIn(email: string, password: string): Promise<User | null> {
  //   return Auth.signIn(email, password).then(async (cognitoUser) => {
  //     if (cognitoUser.challengeName) {
  //       // https://docs.amplify.aws/lib/auth/manageusers/q/platform/js#complete-new-password
  //       return User.fromCognitoWithChallenge(
  //         cognitoUser.username,
  //         cognitoUser,
  //         cognitoUser.challengeName
  //       );
  //     } else {
  //       // happy path
  //       Auth.currentSession()
  //         .then((session) => {
  //           this.setAuthToken(session.getAccessToken().getJwtToken());
  //           if (!this.sessionId) {
  //             this.setSessionId(createId());
  //           }
  //         })
  //         .catch((error) => logger.error(error));
  //       return await this.getUser();
  //     }
  //   });
  // }

  async getAndSetAuthToken(): Promise<string | undefined> {
    let jwt: string | undefined = undefined
    await Auth.currentSession()
      .then((session) => {
        jwt = session.getAccessToken().getJwtToken()
        this.setAuthToken(jwt);
        if (!this.sessionId) {
          this.setSessionId(createId());
        }
      })
      .catch((error) => logger.error(error));
    this.applySessionId();

    return jwt
  }

  async signOut(all = false, excludeSessionStorage = false): Promise<boolean> {
    try {
      if (all) {
        await Auth.signOut({ global: true });
      } else {
        await Auth.signOut();
      }
      this.clearSession(excludeSessionStorage);
    } catch (error) {
      console.error(`${AUTH_ERRORS.signOut}: ${(error as Error)?.message}`);
      return false;
    }
    return true;
  }

  // Delete once fully committed to hosted auth UI
  // async signUp(
  //   email: string,
  //   password: string,
  //   attributes?: CognitoSignUpAttributes
  // ): Promise<boolean> {
  //   try {
  //     const { given_name, middle_name, family_name, phone_number } =
  //       attributes || {};
  //     const inviteId = attributes && attributes["custom:invite_id"];
  //     const { user: cognitoUser } = await Auth.signUp({
  //       username: email,
  //       password,
  //       attributes: {
  //         given_name,
  //         middle_name,
  //         family_name,
  //         phone_number,
  //         "custom:invite_id": inviteId, // possibly undefined
  //       },
  //     });
  //     logger.debug(JSON.stringify(cognitoUser));
  //     if (cognitoUser) {
  //       if (inviteId) {
  //         // make a call to redeem the invite on the backend - places user into groups
  //         if (await inviteApi.redeemInvite(inviteId)) {
  //           return true;
  //         } else {
  //           throw new Error(AUTH_ERRORS.inviteRedemption);
  //         }
  //       } else {
  //         // in case inviteId wasn't present
  //         return true;
  //       }
  //     } else {
  //       throw new Error(AUTH_ERRORS.accountCreation);
  //     }
  //   } catch (error) {
  //     logger.error(error as Error);
  //     throw error;
  //   }
  // }

  // // Delete once fully committed to hosted auth UI
  // async verify(username: string, code: string): Promise<any> {
  //   return await Auth.confirmSignUp(username, code);
  // }
  //
  // // Delete once fully committed to hosted auth UI
  // async resendVerify(username: string): Promise<any> {
  //   return await Auth.resendSignUp(username);
  // }
  //
  // // Delete once fully committed to hosted auth UI
  // async forgotPassword(username: string): Promise<any> {
  //   return Auth.forgotPassword(username);
  // }
  //
  // // Delete once fully committed to hosted auth UI
  // async updatePasswordWithCode(
  //   username: string,
  //   code: string,
  //   newPassword: string
  // ): Promise<any> {
  //   return Auth.forgotPasswordSubmit(username, code, newPassword);
  // }
  //
  // // Delete once fully committed to hosted auth UI
  // async updatePassword(oldPassword: string, newPassword: string): Promise<any> {
  //   return Auth.currentAuthenticatedUser().then((user) => {
  //     return Auth.changePassword(user, oldPassword, newPassword);
  //   });
  // }
  //
  // // Delete once fully committed to hosted auth UI
  // async setNewPassword(
  //   cognitoUser: CognitoUser,
  //   newPassword: string
  // ): Promise<any> {
  //   return Auth.completeNewPassword(cognitoUser, newPassword);
  // }

  // TODO: implement use of resend confirmation code
  // updatePasswordWithCode(code: string, newPassword: string): Promise<void> {
  //   return Auth.currentAuthenticatedUser()
  //     .then((user) => {
  //       // Collect confirmation code and new password, then
  //       return Auth.forgotPasswordSubmit(user.getUsername(), code, newPassword)
  //         .then((data) => console.log(data))
  //         .catch((err) => console.log(err));
  //     })
  //     .then((data) => console.log(data))
  //     .catch((err) => console.log(err));
  // }

  /**
   * most user attributes come from cognito
   *  except roleId, advisorGroupId, advisorGroupName
   */
  async getUser(): Promise<User | null> {
    try {
      return await retryOnException(async (): Promise<User | null> => {
        // Pull in cognito user
        const cognitoResponse = await Auth.currentAuthenticatedUser();
        const groups = cognitoResponse.signInUserSession.idToken.payload["cognito:groups"];
        let user = new User()
        let userFromCognito: User | undefined = undefined
        if (!this.isImpersonationActive()) {
          console.log("pulling base user from cognito")
          userFromCognito = User.fromCognito(cognitoResponse.attributes);
          user.cognitoGroups = groups;
          user.advisorGroupName = this.getAdvisorGroup(groups);
        }

        // Ask api who we are (might be different than cognito says, if uber is impersonating)
        const response = await callGet(User.path + "/me", true)
        if (response === null) {
          throw new Error(AUTH_ERRORS.getUserFailed);
        }
        if (!response?.data) {
          throw new Error(AUTH_ERRORS.laravelNoData);
        }

        // Fill out user object
        user = User.fromJSON(response.data)
        user.hasCompletedOnboarding = response.data?.has_completed_onboarding || response.data?.hasCompletedOnboarding;
        if (response.data?.profile) {
          user.profile = UserProfile.fromJSON(response.data.profile);
          user.profile.id = response.data.profile.id
        } else if (userFromCognito && !this.isImpersonationActive()) {
          // Copy profile data from cognito if we don't have a profile yet
          const userProfileJson: UserProfileJSON = {
            user_id: user.id || userFromCognito.id,
            first_name: user.first || userFromCognito.first,
            middle_name: user.middle || userFromCognito.middle,
            last_name: user.last || userFromCognito.last,
            email: user.email || userFromCognito.email,
            countries_id: user.countryId?.toString() || userFromCognito.countryId?.toString(),
            states_id: user.stateId?.toString() || userFromCognito.stateId?.toString(),
            languages_id: user.languageId?.toString() || userFromCognito.languageId?.toString(),
            phone: user.phone || userFromCognito.phone,
          };
          await AtticusMySQL.updateUserProfile(userProfileJson);
          user.profile = UserProfile.fromJSON(userProfileJson);
        }

        // Update intercom variables if we can
        try {
          const windowObj = window as any;
          windowObj.Intercom("update", {
            name: user.fullName || "",
            email: user.email || "",
            user_id: user.id || -1,
          });
          console.log("intercom vars updated to include email " + user.email);
        } catch (e) {
          console.error(
            "unable to update intercom vars " + (e as any).message
          );
        }

        return user;
      }, 4, 1000)

    } catch (error) {
      logger.error(error as Error);
      throw error;
    }
  }

  getAdvisorGroup(groups: string[] | undefined): string | undefined {
    return groups?.filter((group: string) =>
      group.includes(CONSTANTS.ADVISOR_GROUP_PREFIX)
    )[0];
  }

  async updateAttributes(updates: Record<string, string>): Promise<boolean> {
    const user = await Auth.currentAuthenticatedUser();
    const result = await Auth.updateUserAttributes(user, updates);
    logger.debug(result);
    return true;
  }

  async getSubscriptionStatus(): Promise<any> {
    const response = await callGet("subscription", true, {
      baseURL: laravelApiV1,
    });

    return response?.data.message;
  }

  // async updateAdvisorGroup(
  //   cognitoUsername?: string,
  //   groupName?: string,
  //   rethrow?: false
  // ): Promise<boolean> {
  //   try {
  //     if (cognitoUsername) {
  //       const response = await API.put(
  //         REST_API,
  //         Advisor.path + "/" + cognitoUsername,
  //         {
  //           body: {
  //             group: groupName || "AG-", // indicates removal
  //           },
  //         }
  //       );
  //       if (response?.data) {
  //         return response.data === "success";
  //       } else {
  //         throw Error("no data property");
  //       }
  //     } else {
  //       throw Error("cognitoUsername && groupName required");
  //     }
  //   } catch (error) {
  //     logger.error(error as Error);
  //     if (rethrow) throw error;
  //   }
  //   return false;
  // }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static handleError(error: any, message?: string): void {
    if ((error as any)?.response?.status === 401) {
      logger.error(
        (error as any)?.response?.statusText ||
        message ||
        "error: " + (error as Error)?.toString()
      );
      // requesting missing or invalid resource
      window.document.location.href = SCREEN_PATHS.auth;
    } else {
      logger.error(error as Error);
    }
  }
}
